import React, { useEffect, useState, useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { getAllInvoices, downloadInvoice } from "../../../api/api";
import { SearchContext } from "../../../context/SearchContext";
import styles from "./AdminInvoiceList.module.css";
import Pagination from "../../../components/layout/Pagination/Pagination";

const AdminInvoiceList = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const navigate = useNavigate();
  const { searchTerm = "" } = useContext(SearchContext);
  useEffect(() => {
    const fetchInvoices = async () => {
      try {
        const response = await getAllInvoices();
        setInvoices((response.data || []).filter((inv) => inv.invoice_number));
      } catch (error) {
        console.error("Failed to fetch invoices:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchInvoices();
  }, []);

  const handleBack = () => {
    navigate(-1);
  };

  const handleInvoiceDownload = async (invoiceNumber) => {
    try {
      const blob = await downloadInvoice(invoiceNumber);
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${invoiceNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error("Error downloading invoice:", err);
      alert("Failed to download invoice.");
    }
  };

  const filteredInvoices = useMemo(() => {
    const q = searchTerm.toLowerCase();
    const base = (invoices || []).filter((inv) => inv.invoice_number);
    if (!q) return base;
    return base.filter((inv) => {
      const name = inv.customer_name?.toLowerCase() || "";
      const invoiceNo = inv.invoice_number?.toLowerCase() || "";
      const description = inv.description?.toLowerCase() || "";
      return (
        name.includes(q) || invoiceNo.includes(q) || description.includes(q)
      );
    });
  }, [invoices, searchTerm]);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentInvoices = filteredInvoices.slice(
    indexOfFirstItem,
    indexOfLastItem
  );
  const totalPages = Math.ceil(filteredInvoices.length / itemsPerPage);

  return (
    <div className={styles.invoiceContainer}>
      <div className={styles.topBar}>
        <h3>All Invoices</h3>
      </div>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <table className={styles.invoiceTable}>
          <thead>
            <tr>
              <th>#</th>
              <th>Invoice No</th>
              <th>Customer</th>
              <th>Date</th>
              <th>
                Total (<span className="dirham-symbol">&#xea;</span>)
              </th>
              <th>Status</th>
              <th>Payment Ref</th>
              <th>Description</th>
              <th>Invoice</th>
            </tr>
          </thead>
          <tbody>
            {filteredInvoices.length > 0 ? (
              currentInvoices.map((inv, index) => (
                <tr key={inv.transaction_id}>
                  <td>{indexOfFirstItem + index + 1}</td>
                  <td>{inv.invoice_number}</td>
                  <td>{inv.customer_name}</td>
                  <td>{new Date(inv.transaction_date).toLocaleDateString()}</td>
                  <td>
                    <span className="dirham-symbol">&#xea;</span> {inv.amount}
                  </td>
                  <td>{inv.status}</td>
                  <td>{inv.payment_reference || "N/A"}</td>
                  <td>{inv.description}</td>
                  <td>
                    {inv.invoice_number ? (
                      <button
                        className={styles.downloadButton}
                        onClick={() =>
                          handleInvoiceDownload(inv.invoice_number)
                        }
                      >
                        View
                      </button>
                    ) : (
                      "N/A"
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="9" style={{ textAlign: "center" }}>
                  No invoices found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}

      {filteredInvoices.length > itemsPerPage && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={(page) => setCurrentPage(page)}
        />
      )}

      <button className={styles.backButton} onClick={handleBack}>
        Back
      </button>
    </div>
  );
};

export default AdminInvoiceList;
